import { logger, type GenModelPreset } from '@musedrops/shared';
import { listModelPresets, listModelPresetsByType } from '../data/model-preset';
import {
  getGenPromptRecord,
  updateGenPromptModelPreset,
} from '../data/gen-prompt';
import { ConfigApiService } from './configApi';

/**
 * Service for managing model functions and configurations
 */
export class ModelService {
  private static mapPresetSummary(preset: GenModelPreset): {
    key: string;
    name: string;
  } {
    return { key: preset.key, name: preset.name };
  }

  /**
   * Get list of available text model functions from the backend
   */
  static async getAvailableTextModels(): Promise<
    Array<{ key: string; name: string }>
  > {
    try {
      const presets = await listModelPresetsByType('text');
      return presets.map(ModelService.mapPresetSummary);
    } catch (error) {
      logger.error('Failed to fetch available text models', { error });
      return [];
    }
  }

  /**
   * Get list of all available model presets (all types)
   */
  static async getAvailableModels(): Promise<
    Array<{ key: string; name: string; type: string; voiceSet?: string | null }>
  > {
    try {
      const presets = await listModelPresets();
      return presets.map(preset => ({
        key: preset.key,
        name: preset.name,
        type: preset.type,
        voiceSet: preset.voiceSet ?? null,
      }));
    } catch (error) {
      logger.error('Failed to fetch available models', { error });
      return [];
    }
  }

  /**
   * Get list of available voice model functions from the backend
   */
  static async getAvailableVoiceModels(): Promise<
    Array<{ key: string; name: string }>
  > {
    try {
      const presets = await listModelPresetsByType('voice');
      return presets.map(ModelService.mapPresetSummary);
    } catch (error) {
      logger.error('Failed to fetch available voice models', { error });
      return [];
    }
  }

  /**
   * Get default model list as fallback
   */
  static async getDefaultModels(): Promise<
    Array<{ key: string; name: string }>
  > {
    return ModelService.getAvailableTextModels();
  }

  /**
   * Save model selection to database config
   */
  static async saveModelConfig(
    promptKey: string,
    modelName: string
  ): Promise<void> {
    try {
      await updateGenPromptModelPreset(promptKey, modelName);
    } catch (error) {
      logger.error('Error saving model config', {
        promptKey,
        modelName,
        error,
      });
      throw error;
    }
  }

  /**
   * Load model selection from database config
   */
  static async loadModelConfig(promptKey: string): Promise<string | null> {
    try {
      const record = await getGenPromptRecord(promptKey);
      return record.modelPreset || null;
    } catch (error) {
      logger.error('Failed to load model config', { promptKey, error });
      return null;
    }
  }

  /**
   * Get the debug preset for a given type
   */
  static async getDebugPresetForType(
    type: string
  ): Promise<{ key: string; name: string } | null> {
    try {
      const presets = await listModelPresetsByType(type);
      const debugPreset = presets.find(p => p.isDebug);
      return debugPreset
        ? { key: debugPreset.key, name: debugPreset.name }
        : null;
    } catch (error) {
      logger.error('Failed to get debug preset', { type, error });
      return null;
    }
  }

  /**
   * Check if debug mode is enabled
   */
  static async isDebugModeEnabled(): Promise<boolean> {
    try {
      const { config } = await ConfigApiService.getProcessConfig();
      return config.debug_mode_enabled === true;
    } catch (error) {
      logger.error('Failed to check debug mode', { error });
      return false;
    }
  }

  /**
   * Get debug mode info for a preset type
   * Returns null if debug mode is off or no debug preset is configured
   */
  static async getDebugModeInfo(
    type: string
  ): Promise<{
    isEnabled: boolean;
    debugPreset: { key: string; name: string } | null;
  }> {
    try {
      const [isEnabled, debugPreset] = await Promise.all([
        ModelService.isDebugModeEnabled(),
        ModelService.getDebugPresetForType(type),
      ]);
      return { isEnabled, debugPreset };
    } catch (error) {
      logger.error('Failed to get debug mode info', { type, error });
      return { isEnabled: false, debugPreset: null };
    }
  }
}
